import React, { ReactElement, useContext, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { Close, CheckCircle, ErrorOutline, InfoOutlined } from '@mui/icons-material';
import {
  gray20,
  gray40,
  gray80,
  orange50,
  blue20,
  blue50,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularMd, regularSm, regularXl } from '../../../constants/location';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { getPatternsPaginated } from '../../../api/firebase/firestore/pattern/read';
import { PatternType, ItemListType, ItemListChildType } from '../../../types/pattern';
import { PATTERN_ID_OTHER } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { canViewSharedResource } from '../../../utils/groupAccess';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (patternId: string, itemList: Record<string, unknown>[]) => void;
  currentPatternId?: string;
  companyId: string;
  title?: string;
  usesCredit?: boolean;
  hideWarning?: boolean;
};

// itemListをAPIリクエスト用のsnake_caseに変換
const convertItemListForApi = (itemList: ItemListType[]): Record<string, unknown>[] =>
  itemList.map((item: ItemListType) => ({
    id: item.id,
    parent_value: item.parentValue,
    type: item.type ?? 'string',
    display_width: item.displayWidth ?? 1,
    is_active: item.is_active ?? true,
    is_ocr_target: item.is_ocr_target ?? true,
    extraction_rule: item.extractionRule ?? '',
    output_format: item.outputFormat ?? '',
    children:
      item.children?.map((child: ItemListChildType) => ({
        id: child.id,
        child_value: child.childValue,
        type: child.type ?? 'string',
        display_width: child.displayWidth ?? 1,
        is_active: child.is_active ?? true,
        is_ocr_target: child.is_ocr_target ?? true,
        extraction_rule: child.extractionRule ?? '',
        output_format: child.outputFormat ?? '',
      })) || [],
  }));

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      maxWidth: '560px',
      width: '100%',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
    '@media screen and (max-width:960px)': {
      marginBottom: '12px',
    },
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  description: {
    ...regularMd,
    color: gray80,
    marginBottom: '16px',
  },
  list: {
    maxHeight: '400px',
    overflow: 'auto',
    border: `1px solid ${gray40}`,
    borderRadius: '8px',
  },
  listItem: {
    borderBottom: `1px solid ${gray40}`,
    padding: '12px 16px',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listItemSelected: {
    backgroundColor: `${gray20} !important`,
    borderBottom: `1px solid ${gray40}`,
    padding: '12px 16px',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listItemCurrent: {
    borderBottom: `1px solid ${gray40}`,
    padding: '12px 16px',
    backgroundColor: gray20,
    cursor: 'default',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  patternName: {
    ...boldMd,
    color: gray80,
  },
  patternNameCurrent: {
    ...boldMd,
    color: gray80,
  },
  patternDescription: {
    ...regularSm,
    color: gray80,
    // 長い説明文（連続文字列）でも横あふれさせず折り返す
    overflowWrap: 'anywhere',
  },
  currentBadge: {
    ...regularSm,
    color: gray80,
    marginLeft: '8px',
  },
  otherItem: {
    borderTop: `2px solid ${gray40}`,
    padding: '12px 16px',
  },
  otherItemSelected: {
    backgroundColor: `${gray20} !important`,
    borderTop: `2px solid ${gray40}`,
    padding: '12px 16px',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '16px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
  emptyMessage: {
    ...regularMd,
    color: gray80,
    textAlign: 'center',
    padding: '40px',
  },
};

const PatternSelectModal = ({
  open,
  onClose,
  onSelect,
  currentPatternId,
  companyId,
  title = '仕分け変更',
  usesCredit = false,
  hideWarning = false,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  const { user } = useContext(AuthContext);
  const [patterns, setPatterns] = useState<PatternType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatternId, setSelectedPatternId] = useState<string | null>(null);

  const fetchPatterns = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const result = await getPatternsPaginated(100, null, companyId);
      // 共有範囲によるアクセス制御フィルタ（#2804）。
      // 仕分け変更先には、自分が閲覧可能なパターン（全体公開＋自分の所属グループ）のみ選べる。
      // 運営スタッフは canViewSharedResource 内部で全件許可される（企業管理者は所属グループのみ。#2886）。
      const visible = user
        ? result.results.filter(pattern =>
            canViewSharedResource(
              { uid: user.uid, role: user.role, group_ids: user.group_ids },
              pattern,
            ),
          )
        : result.results;
      setPatterns(visible);
    } catch {
      // パターン取得失敗時は空リストのまま表示
    } finally {
      setLoading(false);
    }
  }, [companyId, user]);

  useEffect(() => {
    if (open) {
      setSelectedPatternId(null);
      fetchPatterns();
    }
  }, [open, fetchPatterns]);

  const handleConfirm = (): void => {
    if (!selectedPatternId) return;

    if (selectedPatternId === PATTERN_ID_OTHER) {
      onSelect(PATTERN_ID_OTHER, []);
      return;
    }

    const selectedPattern = patterns.find(p => p.patternId === selectedPatternId);
    if (!selectedPattern) return;

    onSelect(selectedPatternId, convertItemListForApi(selectedPattern.item_list));
  };

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{title}</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.description}>変更先のパターンを選択してください</Typography>

      {loading ? (
        <Box sx={styles.loadingWrapper}>
          <CircularProgress size={32} />
        </Box>
      ) : patterns.length === 0 ? (
        <Typography sx={styles.emptyMessage}>利用可能なパターンがありません</Typography>
      ) : (
        <List sx={styles.list} disablePadding>
          {patterns.map(pattern => {
            const isCurrent = pattern.patternId === currentPatternId;
            const isSelected = pattern.patternId === selectedPatternId;

            return (
              <ListItemButton
                key={pattern.patternId}
                sx={
                  isCurrent
                    ? styles.listItemCurrent
                    : isSelected
                      ? styles.listItemSelected
                      : styles.listItem
                }
                disabled={isCurrent}
                onClick={(): void => setSelectedPatternId(pattern.patternId)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={isCurrent ? styles.patternNameCurrent : styles.patternName}>
                        {pattern.pattern_name}
                      </Typography>
                      {isCurrent && <Typography sx={styles.currentBadge}>（現在）</Typography>}
                      {isSelected && (
                        <CheckCircle
                          sx={{ color: orange50, marginLeft: '8px', fontSize: '18px' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    pattern.description ? (
                      <Typography sx={styles.patternDescription}>{pattern.description}</Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            );
          })}
          <ListItemButton
            sx={
              currentPatternId === PATTERN_ID_OTHER
                ? styles.listItemCurrent
                : selectedPatternId === PATTERN_ID_OTHER
                  ? styles.otherItemSelected
                  : styles.otherItem
            }
            onClick={(): void => setSelectedPatternId(PATTERN_ID_OTHER)}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ErrorOutline sx={{ color: gray80, marginRight: '8px', fontSize: '18px' }} />
                  <Typography sx={styles.patternName}>パターンなし</Typography>
                  {currentPatternId === PATTERN_ID_OTHER && (
                    <Typography sx={styles.currentBadge}>（現在）</Typography>
                  )}
                  {selectedPatternId === PATTERN_ID_OTHER && (
                    <CheckCircle sx={{ color: orange50, marginLeft: '8px', fontSize: '18px' }} />
                  )}
                </Box>
              }
              secondary={
                <Typography sx={styles.patternDescription}>
                  該当するパターンがない場合に選択してください。読み取りは実行されません。
                </Typography>
              }
            />
          </ListItemButton>
        </List>
      )}

      {!hideWarning && selectedPatternId && selectedPatternId !== PATTERN_ID_OTHER && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: blue20,
            padding: '6px 16px',
            borderRadius: '4px',
            marginTop: '16px',
          }}
        >
          <InfoOutlined sx={{ fontSize: '22px', color: blue50, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '14px',
              color: blue50,
              lineHeight: '1.43',
              letterSpacing: '0.17px',
            }}
          >
            {usesCredit
              ? 'この操作はOCR枚数を消費します。今月の無料利用枠を使い切ったため、OCRクレジットから消費して続行します。'
              : 'この操作はOCR枚数を消費します。'}
          </Typography>
        </Box>
      )}

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button sx={styles.confirmButton} onClick={handleConfirm} disabled={!selectedPatternId}>
          <Typography sx={styles.confirmButtonText}>変更する</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default PatternSelectModal;
